import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// ---- Types ----

export type PlanState = 'draft' | 'locked' | 'executing' | 'completed' | 'cancelled' | 'failed';
export type StepType = 'automated_config' | 'automated_verification' | 'manual_task' | 'manual_verification';
export type StepState = 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';

export interface NetworkPlan {
  id: string;
  organizationId: string;
  contextId: string;
  divisionId: string | null;
  title: string;
  description: string | null;
  state: PlanState;
  createdBy: string;
  lockedAt: string | null;
  lockedBy: string | null;
  stepCount: number;
  completedStepCount: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  isTemplate: boolean;
  cmId: string | null;
  cmTitle: string | null;
}

export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
  doneBy?: string | null;
  doneAt?: string | null;
}

export interface ChecklistItemInput {
  id?: string;
  text: string;
  done?: boolean;
}

export interface StepEvidence {
  id: string;
  stepId: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  createdAt: string;
  downloadUrl: string;
}

export interface PlanStep {
  id: string;
  planId: string;
  position: number;
  stepType: StepType;
  title: string;
  description: string | null;
  deviceId: string | null;
  toolSource: string | null;
  payload: Record<string, unknown>;
  autoVerify: Record<string, unknown> | null;
  assigneeType: string | null;
  assigneeId: string | null;
  state: StepState;
  result: Record<string, unknown> | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  configChangeLogId: string | null;
  expectedAlerts: string[] | null;
  expectedDurationMinutes: number | null;
  overdueNotifiedAt: string | null;
  checklist: ChecklistItem[] | null;
  onFailure: string;
  waitUntil: string | null;
}

export interface PlanWithSteps extends NetworkPlan {
  steps: PlanStep[];
}

interface PlanListResponse {
  plans: NetworkPlan[];
  total: number;
}

export interface CreatePlanRequest {
  contextId: string;
  title: string;
  description?: string;
  isTemplate?: boolean;
}

export interface UpdatePlanRequest {
  title?: string;
  description?: string;
  isTemplate?: boolean;
}

export interface InstantiateTemplateRequest {
  title?: string;
  contextId?: string;
  deviceBindings?: Record<string, string>;
  createChange?: boolean;
}

export interface InstantiateTemplateResponse {
  planId: string;
  cmId: string | null;
}

export interface CreateStepRequest {
  stepType: StepType;
  title: string;
  description?: string;
  deviceId?: string;
  toolSource?: string;
  payload?: Record<string, unknown>;
  autoVerify?: Record<string, unknown>;
  assigneeType?: string;
  assigneeId?: string;
  expectedAlerts?: string[];
  expectedDurationMinutes?: number;
  checklist?: ChecklistItemInput[];
  onFailure?: string;
  soakMinutes?: number;
}

export interface UpdateStepRequest {
  stepType?: StepType;
  title?: string;
  description?: string;
  deviceId?: string;
  toolSource?: string;
  payload?: Record<string, unknown>;
  autoVerify?: Record<string, unknown>;
  assigneeType?: string;
  assigneeId?: string;
  expectedAlerts?: string[];
  expectedDurationMinutes?: number;
  checklist?: ChecklistItemInput[];
  onFailure?: string;
  soakMinutes?: number;
}

export interface ReorderStepRequest {
  newPosition: number;
}

export interface ListPlansFilters {
  state?: string;
  includeArchived?: boolean;
  isTemplate?: boolean;
}

// ---- API functions ----

const backendApi = config.backendApi;

const fetchPlans = async (filters?: ListPlansFilters): Promise<NetworkPlan[]> => {
  const params = new URLSearchParams();
  if (filters?.state) params.append('state', filters.state);
  if (filters?.includeArchived) params.append('include_archived', 'true');
  if (filters?.isTemplate !== undefined) params.append('is_template', String(filters.isTemplate));

  const queryString = params.toString();
  const url = `${backendApi}/api/network-plans${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get<PlanListResponse>(url, { withCredentials: true });
  return response.data.plans;
};

const fetchPlan = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.get<PlanWithSteps>(`${backendApi}/api/network-plans/${planId}`, {
    withCredentials: true,
  });
  return response.data;
};

const createPlan = async (data: CreatePlanRequest): Promise<NetworkPlan> => {
  const response = await axios.post<NetworkPlan>(`${backendApi}/api/network-plans`, data, {
    withCredentials: true,
  });
  return response.data;
};

const updatePlan = async (planId: string, data: UpdatePlanRequest): Promise<NetworkPlan> => {
  const response = await axios.put<NetworkPlan>(`${backendApi}/api/network-plans/${planId}`, data, {
    withCredentials: true,
  });
  return response.data;
};

const deletePlan = async (planId: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/network-plans/${planId}`, { withCredentials: true });
};

const archivePlan = async (planId: string): Promise<void> => {
  await axios.post(`${backendApi}/api/network-plans/${planId}/archive`, {}, { withCredentials: true });
};

const addStep = async (planId: string, data: CreateStepRequest): Promise<PlanStep> => {
  const response = await axios.post<PlanStep>(`${backendApi}/api/network-plans/${planId}/steps`, data, {
    withCredentials: true,
  });
  return response.data;
};

const updateStep = async (planId: string, stepId: string, data: UpdateStepRequest): Promise<PlanStep> => {
  const response = await axios.put<PlanStep>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const deleteStep = async (planId: string, stepId: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/network-plans/${planId}/steps/${stepId}`, {
    withCredentials: true,
  });
};

const reorderStep = async (planId: string, stepId: string, data: ReorderStepRequest): Promise<void> => {
  await axios.post(`${backendApi}/api/network-plans/${planId}/steps/${stepId}/reorder`, data, {
    withCredentials: true,
  });
};

// ---- Execution API functions ----

export interface CompleteStepRequest {
  notes?: string;
  success?: boolean;
}

export interface SkipStepRequest {
  reason?: string;
}

const lockPlan = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(`${backendApi}/api/network-plans/${planId}/lock`, {}, {
    withCredentials: true,
  });
  return response.data;
};

const unlockPlan = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(`${backendApi}/api/network-plans/${planId}/unlock`, {}, {
    withCredentials: true,
  });
  return response.data;
};

const startExecution = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(`${backendApi}/api/network-plans/${planId}/execute`, {}, {
    withCredentials: true,
  });
  return response.data;
};

const cancelExecution = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(`${backendApi}/api/network-plans/${planId}/cancel`, {}, {
    withCredentials: true,
  });
  return response.data;
};

const completeStep = async (planId: string, stepId: string, data: CompleteStepRequest): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/complete`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const skipStep = async (planId: string, stepId: string, data: SkipStepRequest): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/skip`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const retryStep = async (planId: string, stepId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/retry`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const retryPlan = async (planId: string): Promise<PlanWithSteps> => {
  const response = await axios.post<PlanWithSteps>(
    `${backendApi}/api/network-plans/${planId}/retry`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

const toggleChecklistItem = async (
  planId: string,
  stepId: string,
  itemId: string,
  done: boolean,
): Promise<PlanStep> => {
  const response = await axios.post<PlanStep>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/checklist/toggle`,
    { itemId, done },
    { withCredentials: true },
  );
  return response.data;
};

// Step-evidence API helpers + hooks live in `@/features/stepEvidence` so the
// trial build can exclude them (S3-backed, SaaS-only). The `StepEvidence` type
// stays here since the shipped plan types reference it.

// ---- Hooks ----

export function useNetworkPlans(organizationId: string | null | undefined, filters?: ListPlansFilters) {
  const contextReady = useActiveContextReady();

  return useQuery<NetworkPlan[]>({
    queryKey: ['network-plans', organizationId, filters],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('organizationId is required to fetch network plans.');
      }
      return fetchPlans(filters);
    },
    enabled: !!organizationId && contextReady,
  });
}

export function useNetworkPlan(planId: string | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<PlanWithSteps>({
    queryKey: ['network-plan', planId],
    queryFn: () => {
      if (!planId) {
        throw new Error('planId is required to fetch network plan.');
      }
      return fetchPlan(planId);
    },
    enabled: !!planId && contextReady,
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlanRequest) => createPlan(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: UpdatePlanRequest }) => updatePlan(planId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

/** Instantiate a template plan into a concrete plan (optionally wrapping a change). */
export function useInstantiateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ templateId, data }: { templateId: string; data: InstantiateTemplateRequest }) => {
      const response = await axios.post<InstantiateTemplateResponse>(
        `${backendApi}/api/network-plans/${templateId}/instantiate`,
        data,
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
      void queryClient.invalidateQueries({ queryKey: ['change-managements'] });
    },
  });
}

export function useDeletePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => deletePlan(planId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useArchivePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => archivePlan(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useAddStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: CreateStepRequest }) => addStep(planId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useUpdateStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, data }: { planId: string; stepId: string; data: UpdateStepRequest }) =>
      updateStep(planId, stepId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
    },
  });
}

export function useDeleteStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId }: { planId: string; stepId: string }) => deleteStep(planId, stepId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useReorderStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, data }: { planId: string; stepId: string; data: ReorderStepRequest }) =>
      reorderStep(planId, stepId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
    },
  });
}

// ---- Execution Hooks ----

export function useLockPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => lockPlan(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useUnlockPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => unlockPlan(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useStartExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => startExecution(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useCancelExecution() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => cancelExecution(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useCompleteStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, data }: { planId: string; stepId: string; data: CompleteStepRequest }) =>
      completeStep(planId, stepId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useSkipStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, data }: { planId: string; stepId: string; data: SkipStepRequest }) =>
      skipStep(planId, stepId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useRetryStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId }: { planId: string; stepId: string }) => retryStep(planId, stepId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useRetryPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planId: string) => retryPlan(planId),
    onSuccess: (_, planId) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', planId] });
      void queryClient.invalidateQueries({ queryKey: ['network-plans'] });
    },
  });
}

export function useToggleChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, itemId, done }: { planId: string; stepId: string; itemId: string; done: boolean }) =>
      toggleChecklistItem(planId, stepId, itemId, done),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['network-plan', variables.planId] });
    },
  });
}

// useStepEvidence / useUploadEvidence / useDeleteEvidence moved to
// `@/features/stepEvidence` (trial-excludable).
